import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateMessageMutation,
  useGetTeamQuery,
  useGetallStudentQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [recipientSearch, setRecipientSearch] = useState("");
  const debouncedSearch = useDebounce(recipientSearch, 300);

  const [sendMessage, { isLoading }] = useCreateMessageMutation();

  const { data: staffData } = useGetTeamQuery({ searchText: debouncedSearch, id: "" });
  const { data: studentData } = useGetallStudentQuery({ searchText: debouncedSearch, id: "" });

  const recipientList = useMemo(() => {
    const staff = Array.isArray(staffData?.data)
      ? (staffData?.data as any[]).map((s: any) => ({
          id: s.id,
          name: `${s.firstName || ""} ${s.lastName || ""}`,
          type: "Staff",
        }))
      : [];
    const students = Array.isArray(studentData?.data)
      ? (studentData?.data as any[]).map((s: any) => ({
          id: s.id,
          name: `${s.firstName || ""} ${s.lastName || ""}`,
          type: "Student",
        }))
      : [];
    return [...staff, ...students];
  }, [staffData, studentData]);

  const formik = useFormik({
    initialValues: {
      recipientId: "",
      recipientName: "",
      subject: "",
      body: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          recipientId: values.recipientId,
          subject: values.subject,
          body: values.body,
        };

        const response = await sendMessage(payload).unwrap();
        if (response.status) {
          showMessage(response.message || "Message sent successfully");
          resetForm();
          navigate("/message");
        } else {
          showErrorMessage(response.message);
        }
      } catch (error) {
        showErrorMessage("Failed to send message");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.recipientId !== "" &&
      formik.values.subject !== "" &&
      formik.values.body !== ""
    );
  }, [formik.values]);

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>Compose Message</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                      <Typography className={classes.cardTitle}>New Message</Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            To <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Autocomplete
                            options={recipientList}
                            getOptionLabel={(option: any) => `${option.name} (${option.type})`}
                            groupBy={(option: any) => option.type}
                            onInputChange={(e, val) => setRecipientSearch(val)}
                            onChange={(e, val) => {
                              formik.setFieldValue("recipientId", val?.id || "");
                              formik.setFieldValue("recipientName", val?.name || "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search recipient..."
                                size="small"
                                className={classes.textField}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Subject <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Message subject"
                            size="small"
                            {...formik.getFieldProps("subject")}
                            className={classes.textField}
                            InputProps={{ sx: { fontSize: 14 } }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Message <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Type your message..."
                            size="small"
                            multiline
                            rows={12}
                            {...formik.getFieldProps("body")}
                            className={classes.discriptionTextField}
                            InputProps={{ sx: { fontSize: 14 } }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => navigate("/message")}
                      >
                        Discard
                      </Button>
                      <Box m={0.5}></Box>
                      <Button
                        variant="contained"
                        startIcon={isLoading ? <CircularProgress size={18} className={classes.isLoading} /> : <SendIcon />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Send
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
